import { useState, useEffect } from 'react';
import { X, GitBranch, Building2, User, Flag, Calendar, Sparkles, DollarSign, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { safeToISOString } from '../lib/dateUtils';
import { showSuccess, showError } from '../lib/alerts';
import apiClient from '../api/client';
import ModalContainer from './layout/ModalContainer';
import { FormGroup, FormLabel, FormInput, FormSelect, FormTextarea } from './ui/FormComponents';

export default function NewProjectModal({ isOpen, onClose, onSuccess, initialStatus }: { isOpen: boolean, onClose: () => void, onSuccess?: (created?: any) => void, initialStatus?: string }) {
  const { user, dbUser, activeWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const currentPlan = dbUser?.currentPlan || 'free';
  
  const [formData, setFormData] = useState({
    name: '',
    customerId: '',
    priority: 'Média',
    startDate: '',
    endDate: '',
    budget: '',
    owner: user?.displayName || user?.email || 'Sem dono',
    code: '',
    description: ''
  });
  const [companies, setCompanies] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState<any>(null);
  const [lastPayload, setLastPayload] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
      fetchMembers();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    try {
      const res = await apiClient.get('/api/customers').then(r => r.data);
      if (res.ok) {
        const json = await res.json();
        const list = Array.isArray(json)
          ? json
          : Array.isArray(json?.companies)
          ? json.companies
          : Array.isArray(json?.data)
          ? json.data
          : [];
        setCompanies(list);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await apiClient.get('/api/users/members').then(r => r.data);
      if (res.ok) {
        const json = await res.json();
        const list = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
        setMembers(list);
      }
    } catch (err) {
      console.error('Failed to load members', err);
    }
  };

  const getStatusName = (columnId: string) => {
    if (columnId === 'backlog') return 'Backlog';
    if (columnId === 'planejamento') return 'Planejamento';
    if (columnId === 'desenvolvimento') return 'Em Andamento';
    if (columnId === 'testes') return 'Testes';
    if (columnId === 'homologacao') return 'Homologacao';
    if (columnId === 'producao') return 'Producao';
    if (columnId === 'concluido') return 'Concluido';
    return 'Planejamento';
  };

  const mapStatusToApi = (status: string) => {
    const value = status.toString().trim().toLowerCase();
    if (value.includes('planej')) return 'PLANNED';
    if (value.includes('em andamento') || value === 'ativo' || value === 'ativa') return 'ACTIVE';
    if (value.includes('paus')) return 'ON_HOLD';
    if (value.includes('conclu') || value.includes('finalizada')) return 'COMPLETED';
    if (value.includes('cancel')) return 'CANCELLED';
    return status.toUpperCase().replace(/\s+/g, '_');
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    if (!formData.name) return;
    setLoading(true);
    setLastError(null);
    setLastPayload(null);
    try {
      const payload: any = {
        name: formData.name,
        ...(formData.customerId && formData.customerId !== '0' ? { customerId: formData.customerId } : {}),
        description: (formData as any).description ? (formData as any).description : formData.owner ? `Owner: ${formData.owner}` : undefined,
        ...(formData.startDate ? { startDate: safeToISOString(formData.startDate) } : {}),
        ...(formData.endDate ? { endDate: safeToISOString(formData.endDate) } : {}),
        ...(formData.code ? { code: (formData as any).code } : {}),
        ...(formData.budget ? { budget: Number(formData.budget) } : {}),
        status: mapStatusToApi(initialStatus ? getStatusName(initialStatus) : 'Planejamento')
      };
      setLastPayload(payload);
      const { data: createdRaw } = await apiClient.post('/api/projects', payload);
      const created = createdRaw?.data || createdRaw?.project || createdRaw || null;

      try {
        const key = ['projects', activeWorkspace?.id];
        queryClient.setQueryData(key, (old: any) => {
          if (!old) return created ? [created] : [];
          const arr = Array.isArray(old) ? old : (Array.isArray(old?.projects) ? old.projects : []);
          const exists = arr.find((p: any) => p.id === created?.id);
          if (created && !exists) return [created, ...arr];
          return arr;
        });
        queryClient.invalidateQueries({ queryKey: ['projects', activeWorkspace?.id] });
      } catch (e) {
      }

      onSuccess?.(created);
        onClose();
        setLastError(null);
        setLastPayload(null);
        setFormData({ name: '', customerId: '', priority: 'Média', startDate: '', endDate: '', budget: '', owner: user?.displayName || user?.email || 'Sem dono', code: '', description: '' });
        (document.activeElement as HTMLElement)?.blur?.();
        showSuccess('Projeto criado com sucesso!');
      } catch (err) {
        console.error(err);
        const apiError = (err as any)?.apiResponse;
        const normalizedError = apiError || {
          message: err instanceof Error ? err.message : 'Erro ao criar projeto.',
        };
        setLastError(normalizedError);
        const message = normalizedError?.message || 'Erro ao criar projeto.';
        showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} maxWidth="max-w-2xl">
      {/* Header */}
      <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <GitBranch size={14} strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#111111]">Novo Projeto</h3>
            <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Inicie um novo ciclo operacional estratégico</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <FormGroup>
            <FormLabel>Descrição</FormLabel>
            <FormTextarea
              placeholder="Descrição do projeto..."
              value={(formData as any).description || ''}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </FormGroup>
        </div>
        <button 
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
        >
          <X size={15} strokeWidth={2.5} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5 text-left">
        {/* SaaS Warning for Free Plan */}
        {currentPlan === 'free' && (
          <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl flex items-start gap-3">
            <Sparkles size={14} className="text-indigo-500 mt-0.5 flex-shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-bold text-[#111111] uppercase tracking-wider">Limite do Plano Free</span>
              <span className="text-[10px] font-medium text-[#64748B] leading-relaxed">
                Você está usando a versão de Teste. Recursos avançados de IA e White Label estão desativados.
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup>
            <FormLabel required>Nome do Projeto</FormLabel>
            <FormInput 
              required
              placeholder="Ex: Redesign Dashboard" 
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Cliente / Empresa</FormLabel>
            <FormSelect 
                value={formData.customerId}
                onChange={(e) => setFormData({...formData, customerId: e.target.value})}
            >
              <option value="">Selecionar Cliente</option>
              {Array.isArray(companies) && companies.map((c: any, idx: number) => (
                <option key={c.id ?? c.uid ?? c.name ?? idx} value={c.id ?? c.uid ?? idx}>{c.name ?? c.displayName ?? `Empresa ${idx+1}`}</option>
              ))}
            </FormSelect>
          </FormGroup>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup>
            <FormLabel>Prioridade</FormLabel>
            <FormSelect 
              value={formData.priority}
              onChange={(e) => setFormData({...formData, priority: e.target.value})}
            >
              <option value="Alta">Alta</option>
              <option value="Média">Média</option>
              <option value="Baixa">Baixa</option>
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>Prazo</FormLabel>
            <FormInput 
              type="date" 
              value={formData.endDate}
              onChange={(e) => setFormData({...formData, endDate: e.target.value})}
            />
          </FormGroup>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup>
            <FormLabel>Faturamento Pretendido (R$)</FormLabel>
            <FormInput 
              type="number" 
              placeholder="Ex: 5000.00" 
              value={formData.budget}
              onChange={(e) => setFormData({...formData, budget: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Código (opcional)</FormLabel>
            <FormInput
              placeholder="Ex: PRJ-2026-001"
              value={(formData as any).code || ''}
              onChange={(e) => setFormData({...formData, code: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Start Date</FormLabel>
            <FormInput
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({...formData, startDate: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Coordenador / Owner</FormLabel>
            <FormSelect
              value={formData.owner}
              onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
            >
              <option value="">Selecionar Owner</option>
              {Array.isArray(members) && members.map((m: any, idx: number) => (
                <option key={m.id ?? idx} value={(m.User && m.User.fullName) || m.User?.email || m.id}>{(m.User && (m.User.fullName || m.User.email)) || m.User?.email || m.id}</option>
              ))}
            </FormSelect>
          </FormGroup>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#0F172A05]">
          <button 
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button 
            type="submit"
            disabled={loading || !formData.name}
            className="px-5 py-2 text-xs font-bold text-white bg-[#111111] hover:bg-[#222222] rounded-xl shadow-sm hover:scale-[1.01] transition-all cursor-pointer flex items-center gap-1.5"
          >
            {loading ? 'Criando...' : (
              <>
                <Check size={14} strokeWidth={2.5} />
                <span>Criar Projeto</span>
              </>
            )}
          </button>
        </div>
        {/* Backend error details panel */}
        {lastError && (
          <div className="mt-4 p-4 bg-rose-50 border border-rose-100 rounded-xl text-left">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-sm font-bold text-rose-700">Erro ao criar projeto</h4>
                <p className="text-xs text-rose-600 mt-1">{(lastError?.apiResponse?.message) || lastError?.message || 'Erro desconhecido.'}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const details = {
                      error: lastError?.apiResponse || lastError?.message || lastError,
                      payload: lastPayload || null,
                    };
                    try {
                      navigator.clipboard.writeText(JSON.stringify(details, null, 2));
                      showSuccess('Detalhes copiados para a área de transferência.');
                    } catch (e) {
                      showError('Não foi possível copiar.');
                    }
                  }}
                  className="px-3 py-2 text-xs font-bold bg-white border border-rose-200 rounded-xl text-rose-700"
                >
                  Copiar detalhes
                </button>
                <button
                  onClick={() => { setLastError(null); setLastPayload(null); }}
                  className="px-3 py-2 text-xs font-bold bg-white border border-rose-200 rounded-xl text-rose-700"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </ModalContainer>
  );
}
